const INTEREST_TYPE_LABELS: Record<string, string> = {
  fixed: "Stałe",
  variable: "Zmienne",
  periodically_fixed: "Okresowo-stałe",
};

const LOAN_TYPE_LABELS: Record<string, string> = {
  borrowed: "Zaciągnięta",
  lent: "Udzielona",
};

const PAYMENT_LABELS: Record<string, string> = {
  monthly: "Miesięczne",
  quarterly: "Kwartalne",
  yearly: "Roczne",
  at_end_of_year: "Na koniec roku",
  at_end: "Na koniec roku",
};

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  apartment: "Mieszkanie",
  house: "Dom",
  land: "Działka",
};

const METAL_TYPE_LABELS: Record<string, string> = {
  gold: "Złoto",
  silver: "Srebro",
};

const detailsFields = [
  "area",
  "weight",
  "cut",
  "color",
  "clarity",
  "fineness",
  "artworkType",
  "author",
  "eraAge",
  "interestType",
  "interestPayment",
  "contractDate",
  "durationDate",
  "premiumPayment",
  "autoRenewal",
  "publicationDate",
];

export const formatNumericWithSpaces = (value: any): string | undefined => {
  if (value == null || value === "") return undefined;
  const str = String(value);
  const dotIndex = str.indexOf(".");
  const intPart = dotIndex >= 0 ? str.slice(0, dotIndex) : str;
  let decPart = dotIndex >= 0 ? str.slice(dotIndex) : "";
  if (!/^\d+$/.test(intPart)) return str;
  if (decPart.length === 2) decPart += "0";
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");
  return formattedInt + decPart;
};

export const formatPL = (val: number, maxFrac = 2): string =>
  Number(val)
    .toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxFrac,
    })
    .replace(/,/g, "\u00A0");

export const formatValue = (
  item: any,
  column: string,
  slug: string,
  industryMap: Record<string, string>,
): any => {
  const isStocksOrShares = slug === "stocks" || slug === "shares";

  if (column === "currencyUuid") {
    const currency = item.currency?.currency;
    return currency ?? "Brak";
  }

  if (column === "industry") {
    if (item.industryUuid) {
      return industryMap[item.industryUuid] ?? "Brak";
    }
    return item.industry?.name ?? item.details?.industryName ?? "Brak";
  }

  if (column === "sharesCount") {
    const val = item.sharesCount;
    if (val === null || val === undefined) return "Brak";
    return formatPL(Number(val), 0);
  }

  if (column === "quantity") {
    const val = item.quantity;
    if (val === null || val === undefined) return "Brak";
    if (slug === "crypto") {
      const num = Number(val);
      return num
        .toLocaleString("en-US", {
          minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
          maximumFractionDigits: 8,
        })
        .replace(/,/g, "\u00A0");
    }
    return formatPL(Number(val), 0);
  }

  if (column === "sharesValue") {
    const val = item.sharesValue;
    if (val === null || val === undefined) return "Brak";
    const formatted = formatPL(Number(val));
    const currency = item.currency?.currency ?? "";
    return currency ? `${formatted} ${currency}` : formatted;
  }

  if (column === "publicationDate" && isStocksOrShares) {
    const dateVal = item.publicationDate ?? item.details?.publicationDate;
    if (!dateVal) return "Brak";
    const d = new Date(dateVal as string);
    return isNaN(d.getTime()) ? String(dateVal) : d.toLocaleDateString("pl-PL");
  }

  if (column === "name" && slug === "currencies") {
    return item.currency?.currency ?? "Brak";
  }

  if (
    column === "interestRate" &&
    (slug === "treasury_bonds" || slug === "loans")
  ) {
    const value = item.details?.interestRate;
    if (value === null || value === undefined || value === "") return "Brak";
    return `${value}%`;
  }

  if (column === "entity" && slug === "loans") {
    const val = item.details?.entity ?? item.entity;
    if (val === null || val === undefined || val === "") return "Brak";
    return val;
  }

  if (column === "loanType") {
    const value = item.loanType ?? item.details?.loanType;
    if (value === null || value === undefined || value === "") return "Brak";
    return LOAN_TYPE_LABELS[value] ?? value;
  }

  if (column === "interestType") {
    const value = item.details?.interestRateType ?? item.details?.interestType;
    if (value === null || value === undefined || value === "") return "Brak";
    return INTEREST_TYPE_LABELS[value] ?? value;
  }

  if (column === "name" && slug === "real_estate") {
    const propertyType = item.details?.propertyType;
    if (!propertyType) return item.name ?? "Brak";
    if (propertyType === "other") {
      return item.details?.propertyTypeOther ?? "Brak";
    }
    return PROPERTY_TYPE_LABELS[propertyType] ?? propertyType;
  }

  if (column === "name" && slug === "precious_metals") {
    const metalType = item.details?.metalType;
    if (!metalType) return item.name ?? "Brak";
    if (metalType === "other") {
      return item.details?.metalTypeOther ?? "Brak";
    }
    return METAL_TYPE_LABELS[metalType] ?? metalType;
  }

  if (column === "city") {
    const val = item.details?.city;
    if (val === null || val === undefined || val === "") return "Brak";
    return val;
  }

  if (column === "pricePerSqm") {
    const sqmValue = item.details?.pricePerSqm;
    if (sqmValue === null || sqmValue === undefined) return "Brak";
    return formatPL(Number(sqmValue));
  }

  if (slug === "investment_policy") {
    if (column === "contractDate") {
      const dateVal = item.details?.contractDate;
      if (!dateVal) return "Brak";
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? dateVal : d.toLocaleDateString("pl-PL");
    }

    if (column === "durationDate") {
      const dateVal = item.details?.durationDate;
      if (dateVal === null || dateVal === undefined) return "Bezterminowo";
      if (typeof dateVal === "string" && dateVal !== "") {
        const d = new Date(dateVal);
        if (isNaN(d.getTime())) return "Brak";
        if (
          d.getFullYear() === 2000 &&
          d.getMonth() === 0 &&
          d.getDate() === 1
        )
          return "Brak";
        return d.toLocaleDateString("pl-PL");
      }
      return "Brak";
    }
  }

  if (slug === "loans") {
    if (column === "contractDate") {
      const dateVal = item.details?.contractDate;
      if (!dateVal) return "Brak";
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? dateVal : d.toLocaleDateString("pl-PL");
    }

    if (column === "durationDate") {
      const dateVal = item.details?.durationDate;
      if (dateVal === null || dateVal === undefined) return "Bezterminowo";
      if (typeof dateVal === "string" && dateVal !== "") {
        const d = new Date(dateVal);
        if (isNaN(d.getTime())) return "Brak";
        if (
          d.getFullYear() === 2000 &&
          d.getMonth() === 0 &&
          d.getDate() === 1
        )
          return "Brak";
        return d.toLocaleDateString("pl-PL");
      }
      return "Brak";
    }
  }

  const value = detailsFields.includes(column)
    ? item.details?.[column]
    : item[column];

  if (value === null || value === undefined || value === "") return "Brak";

  if (column === "interestRate") return `${value}%`;
  if (column === "interestPayment" || column === "premiumPayment")
    return PAYMENT_LABELS[value] ?? value;
  if (column === "autoRenewal")
    return value === true || value === 1 ? "Tak" : "Nie";
  if (column === "primaryValue") {
    const numVal = typeof value === "number" ? value : Number(value);
    if (!isNaN(numVal)) {
      return numVal
        .toLocaleString("en-US", {
          minimumFractionDigits: Number.isInteger(numVal) ? 0 : 2,
          maximumFractionDigits: 2,
        })
        .replace(/,/g, "\u00A0");
    }
    return value;
  }
  if (column === "weight") {
    return `${formatNumericWithSpaces(value) ?? value} g`;
  }
  if (
    column === "fineness" ||
    column === "cut" ||
    column === "color" ||
    column === "clarity"
  ) {
    return value;
  }
  if (column === "area") {
    return typeof value === "number" ? formatPL(value) : value;
  }
  if (column === "mass") return `${value} g`;

  return value;
};
